import { Box, Button, Group } from '@mantine/core';
import { IconChartBar } from '@tabler/icons-react';
import { lazy, Suspense, type FC } from 'react';
import { Link } from 'react-router';
import { useHasMetricsInCatalog } from '../../features/metricsCatalog/hooks/useMetricsCatalog';
import Omnibar from '../../features/omnibar';
import { useOptionalProjectRoute } from '../../hooks/useProjectRoute';
import useApp from '../../providers/App/useApp';
import MantineIcon from '../common/MantineIcon';
import { AutopilotNavButton } from './AutopilotNavButton';
import BrowseMenu from './BrowseMenu';
import ExploreMenu from './ExploreMenu';
import HeadwayMenuItem from './HeadwayMenuItem';
import HelpMenu from './HelpMenu';
import classes from './MainNavBarContent.module.css';
import { MetricsLink } from './MetricsLink';
import { NotificationsMenu } from './NotificationsMenu';
import ProjectSwitcher from './ProjectSwitcher';
import SettingsMenu from './SettingsMenu';
import UserCredentialsSwitcher from './UserCredentialsSwitcher';
import UserMenu from './UserMenu';

const AiAgentsButton = lazy(() =>
    import('./AiAgentsButton').then((module) => ({
        default: module.AiAgentsButton,
    })),
);

type Props = {
    activeProjectUuid: string | undefined;
    activeProjectUrlIdentifier: string | undefined;
    isLoadingActiveProject: boolean;
};

export const MainNavBarContent: FC<Props> = ({
    activeProjectUuid,
    activeProjectUrlIdentifier,
    isLoadingActiveProject,
}) => {
    const projectRoute = useOptionalProjectRoute();
    const projectUrlIdentifier =
        projectRoute?.projectUrlIdentifier ?? activeProjectUrlIdentifier;
    const homeUrl = activeProjectUuid
        ? `/projects/${projectUrlIdentifier}/home`
        : '/';
    // DL Fork: deployments can hide individual navbar items entirely via
    // VITE_SHOW_* env flags (default 'true'). Queries are skipped too so a
    // hidden item costs nothing.
    const showMetrics = import.meta.env.VITE_SHOW_METRICS !== 'false';
    const { data: hasMetrics } = useHasMetricsInCatalog({
        projectUuid: showMetrics ? activeProjectUuid : undefined,
    });
    const { health } = useApp();
    const headwayEnabled = health.data?.headway?.enabled;

    // DL Fork: report-focused desktop layout replaces the product logo with
    // a neutral home link, lets search grow, and keeps actions on the right.
    return (
        <Group align="center" wrap="nowrap" w="100%">
            <Group
                align="center"
                gap="sm"
                wrap="nowrap"
                className={classes.leftGroup}
            >
                <Button
                    component={Link}
                    to={homeUrl}
                    variant="subtle"
                    color="gray"
                    size="md"
                    radius="md"
                    px="sm"
                    leftSection={<MantineIcon icon={IconChartBar} size="lg" />}
                    fz="sm"
                    fw={600}
                    c="ldGray.6"
                    style={{ flexShrink: 0 }}
                >
                    Reporting
                </Button>

                {!isLoadingActiveProject && activeProjectUuid && (
                    <Box className={classes.omnibar}>
                        <Omnibar projectUuid={activeProjectUuid} />
                    </Box>
                )}
            </Group>

            <Group className={classes.rightGroup} gap="xs" wrap="nowrap">
                {!isLoadingActiveProject && activeProjectUuid && (
                    <Group gap="xs" wrap="nowrap">
                        <ExploreMenu
                            projectUuid={activeProjectUuid}
                            projectUrlIdentifier={projectUrlIdentifier}
                        />
                        <BrowseMenu projectUuid={activeProjectUuid} />
                        {showMetrics && hasMetrics && (
                            <MetricsLink projectUuid={activeProjectUuid} />
                        )}
                        <Suspense fallback={null}>
                            <AiAgentsButton projectUuid={activeProjectUuid} />
                        </Suspense>
                        <AutopilotNavButton projectUuid={activeProjectUuid} />
                    </Group>
                )}

                <Group gap="xs" wrap="nowrap">
                    {/* DL Fork: each navbar item is individually hideable via
                        VITE_SHOW_* env flags for embedded deployments. */}
                    {import.meta.env.VITE_SHOW_SETTINGS !== 'false' && (
                        <SettingsMenu />
                    )}

                    {!isLoadingActiveProject && activeProjectUuid && (
                        <NotificationsMenu projectUuid={activeProjectUuid} />
                    )}

                    {import.meta.env.VITE_SHOW_HELP !== 'false' && <HelpMenu />}

                    {import.meta.env.VITE_SHOW_HEADWAY !== 'false' &&
                        headwayEnabled &&
                        !isLoadingActiveProject &&
                        activeProjectUuid && (
                            <HeadwayMenuItem projectUuid={activeProjectUuid} />
                        )}

                    {import.meta.env.VITE_SHOW_PROJECT_SWITCHER !== 'false' && (
                        <ProjectSwitcher />
                    )}

                    {import.meta.env.VITE_SHOW_USER_CREDENTIALS_SWITCHER !==
                        'false' && <UserCredentialsSwitcher />}
                </Group>

                {import.meta.env.VITE_SHOW_USER_MENU !== 'false' && (
                    <UserMenu />
                )}
            </Group>
        </Group>
    );
};
